// Plan banner — tells the tenant where they stand and what the offer is.
//
// Three states, in priority order:
//   1. Free month running   → days left on Pro/Business before billing starts.
//   2. Free plan, offer open → "first month free" invitation to upgrade.
//   3. Nothing              → paying customer, or the offer is already used.
//
// Deliberately not dismissible-forever: it is a single line, and a tenant who
// dismisses the countdown then gets charged has a legitimate complaint. It IS
// dismissible for the session so it never blocks work.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../lib/i18n';
import { useBilling } from '../../lib/billing';

const DAY_MS = 24 * 60 * 60 * 1000;

export default function PlanBanner() {
  const { t } = useI18n();
  const b = useBilling();
  const [dismissed, setDismissed] = useState(false);

  if (b.loading || dismissed) return null;

  const onFreePlan = Boolean(b.plan?.isFree);
  const freeMonthRunning = b.status === 'trialing' && !onFreePlan && b.renewalAt != null;
  const daysLeft = b.renewalAt ? Math.max(0, Math.ceil((b.renewalAt - Date.now()) / DAY_MS)) : 0;

  // Only pitch the free month when it is actually still available.
  const offerOpen = onFreePlan && !b.freeMonthUsed;

  if (!freeMonthRunning && !offerOpen) return null;

  const body = freeMonthRunning
    ? t('bill.banner.freeMonthRunning', { days: daysLeft }) || `Your free month ends in ${daysLeft} days.`
    : t('bill.banner.freeMonthOffer') || 'Get your first month free on any paid plan — no credit card.';

  return (
    <div className="flex items-center gap-3 bg-success px-4 py-2.5 text-white sm:px-6">
      <span aria-hidden>🎁</span>
      <p className="min-w-0 flex-1 text-[13px] font-semibold">{body}</p>
      <Link
        to="/billing"
        className="flex-shrink-0 rounded-full bg-white/20 px-3 py-1 text-[12px] font-extrabold text-white hover:bg-white/30"
      >
        {freeMonthRunning ? t('bill.banner.manage') : t('bill.plan.upgrade')}
      </Link>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('common.dismiss')}
        className="flex-shrink-0 text-white/70 hover:text-white"
      >
        ✕
      </button>
    </div>
  );
}
